import { AxiosResponse } from 'axios';
import { useLocation } from 'react-router';
import { Reducer } from 'redux';
import { createRoutine } from 'redux-saga-routines';
import { put, takeLatest } from 'redux-saga/effects';

import { EProcessStatus, IAsyncData, Maybe } from './models';
import { IFSAAction } from './store/models';

// tslint:disable-next-line:typedef
function* sagaWorker<T> (action: IFSAAction<any>) {
    const routine = createRoutine(action.type);

    try {
        yield put(routine.request());
        const response: AxiosResponse<T> = yield action.payload;

        yield put(routine.success(response.data ? response.data : {}));
    } catch (error) {
        yield put(routine.failure(error.message));
    }
}

// tslint:disable-next-line:typedef
export function* generateAsyncSagaWatcher<T> (
    actionName: string,
) {
    yield takeLatest(actionName, (action: IFSAAction<any>) => sagaWorker<T>(action));
}

export function getInitialAsyncData<T> (initialData: Maybe<T> = null): IAsyncData<T> {
    return {
        data: initialData,
        error: null,
        status: EProcessStatus.IDLE,
    };
}

// tslint:disable-next-line:typedef
export function generateAsyncReducer<T> (
    actionType: string,
): Reducer<IAsyncData<T>, IFSAAction<T>> {
    return (
        state: IAsyncData<T> = getInitialAsyncData<T>(),
        action: IFSAAction<T>,
    ): IAsyncData<T> => {
        const routineAction = createRoutine(actionType);

        switch (action.type) {
            case routineAction.TRIGGER:
                return state;
            case routineAction.SUCCESS:
                const payload = action.payload ? ((action.payload as any).data ? (action.payload as any).data : action.payload) : action.payload;
                return {
                    ...state,
                    data: payload,
                    status: EProcessStatus.SUCCESS
                };
            case routineAction.FAILURE:
                return {
                    ...state,
                    status: EProcessStatus.ERROR,
                    error: action.payload,
                };
            case routineAction.REQUEST:
                return {
                    ...state,
                    status: EProcessStatus.PENDING,
                };
            default:
                return state;
        }
    };
}

export const isSuccess = (branch: IAsyncData<any>) => branch.status === EProcessStatus.SUCCESS;
export const isInitial = (branch: IAsyncData<any>) => branch.status === EProcessStatus.IDLE;
export const isPending = (branch: IAsyncData<any>) => branch.status === EProcessStatus.PENDING;
export const isError = (branch: IAsyncData<any>) => branch.status === EProcessStatus.ERROR;

export const getYoutubeVideoId = (url: string): RegExpExecArray => {
    const reg: RegExp = new RegExp('[?&]v=([^&#]*)', 'i');
    return reg.exec(url) as RegExpExecArray;
};

export function makePath<P> (url: string, params: P): string {
    let path = url;

    Object.entries(params).forEach(([key, value]) => {
        path = path.replace(`:${key}`, value);
    });

    return path;
}

export function useQuery (): URLSearchParams {
    return new URLSearchParams(useLocation().search);
}
